import { CourseOption, CourseTakingStatus, CourseUserState } from "../types/course.response";
import { getBackendCourseKey } from "../utils/courseUtils";

/**
 * 用户课程条目的增量缓存(契约 §3.4 迁移第四步的 b/c 两步),
 * keyed by 后端 courses 字典口径的 courseKey。
 *
 * - b 双写:每次 mutation 的响应 data(完整 CourseOption,含 userItemId 与展示数据)
 *   写入;DELETE 按 userItemId 删。API 层(user-courses/exams/custom-items)统一接线,
 *   页面无感。
 * - c 对拍:轻量刷新拿到详情档后,`reconcileUserItems` 把"缓存 ∩ userStates 重建"
 *   与并行 GET 的真相源比对,diff 记 console.warn,并用 GET 结果整体校准缓存。
 * - d 切换(未做)前缓存只写不消费;并行 GET 仍是真相源。
 */
const cache = new Map<string, CourseOption>();

export function putUserItem(item: CourseOption): void {
    cache.set(getBackendCourseKey(item), item);
}

export function removeUserItemById(userItemId: number): void {
    for (const [key, item] of Array.from(cache)) {
        if (item.userItemId === userItemId) {
            cache.delete(key);
            return;
        }
    }
}

/** 身份切换(换账号/登入登出)时清空防串号 */
export function clearUserItemCache(): void {
    cache.clear();
}

export function getUserItemCacheSnapshot(): ReadonlyMap<string, CourseOption> {
    return cache;
}

/** 同 key 折叠口径(契约 §3.4):同课多学期取状态优先级最高的行 */
export const STATUS_PRIORITY: Record<CourseTakingStatus, number> = {
    COMPLETED: 4,
    IN_PROGRESS: 3,
    PLANNED: 2,
    SAVED: 1,
    NOT_ON_SCHEDULE: 0,
};

export interface UserItemsDiff {
    /** userStates 有、并行 GET 折叠后没有(排除 combined 兄弟 key;可能为 CUSTOM 或跨端新增) */
    missingInFetch: string[];
    /** 并行 GET 有、userStates 没有(契约 §3.2-1 要求全覆盖,出现即口径问题) */
    missingInUserStates: string[];
    /** 共同 key 上的字段不一致 */
    mismatches: {
        key: string;
        field: "status" | "semester" | "userItemId";
        userStatesValue: unknown;
        fetchedValue: unknown;
    }[];
}

/**
 * c 步双读对拍 + 缓存校准。fetchedItems = 并行 GET 的逐行结果(courses + exams),
 * userStates 来自刚收到的详情档(覆盖该用户全部课程)。
 * 返回 diff 供测试/上报;diff 非空时聚合打一条 console.warn。
 */
export function reconcileUserItems(
    userStates: Record<string, CourseUserState>,
    fetchedItems: CourseOption[],
): UserItemsDiff {
    // 逐行 → 按 key 折叠,与 userStates 的折叠口径对齐
    const fetchedByKey = new Map<string, CourseOption>();
    // combined 组合课:用户一行课在 userStates 里是多个 key,兄弟 key 缺行是预期内
    const combinedSiblingKeys = new Set<string>();
    for (const item of fetchedItems) {
        const key = getBackendCourseKey(item);
        const existing = fetchedByKey.get(key);
        if (!existing || STATUS_PRIORITY[item.userState.status] > STATUS_PRIORITY[existing.userState.status]) {
            fetchedByKey.set(key, item);
        }
        item.combinedCourseInfo?.combinedCourseIds?.forEach(id => combinedSiblingKeys.add(id));
    }

    const diff: UserItemsDiff = { missingInFetch: [], missingInUserStates: [], mismatches: [] };

    for (const [key, state] of Object.entries(userStates)) {
        const fetched = fetchedByKey.get(key);
        if (!fetched) {
            if (!combinedSiblingKeys.has(key)) diff.missingInFetch.push(key);
            continue;
        }
        if (fetched.userState.status !== state.status) {
            diff.mismatches.push({
                key, field: "status",
                userStatesValue: state.status, fetchedValue: fetched.userState.status,
            });
        }
        if (fetched.userState.semester !== state.semester) {
            diff.mismatches.push({
                key, field: "semester",
                userStatesValue: state.semester, fetchedValue: fetched.userState.semester,
            });
        }
        // mutation 双写的条目 vs 刚拉的全量:验证 §4.2 data 的 userItemId / key 口径
        const cached = cache.get(key);
        if (cached && cached.userItemId !== fetched.userItemId) {
            diff.mismatches.push({
                key, field: "userItemId",
                userStatesValue: cached.userItemId, fetchedValue: fetched.userItemId,
            });
        }
    }
    for (const key of Array.from(fetchedByKey.keys())) {
        if (!userStates[key]) diff.missingInUserStates.push(key);
    }

    if (diff.missingInFetch.length || diff.missingInUserStates.length || diff.mismatches.length) {
        console.warn("[userCourseCache] 对拍 diff(契约 §3.4-c,切换前需清零)", diff);
    }

    // 校准:GET 仍是真相源,折叠结果整体覆盖;userStates 没有的 key 剔除(删除语义),
    // 但 GET 覆盖不到的类型(CUSTOM)只要 userStates 记录在就保留 mutation 写入的条目
    fetchedByKey.forEach((item, key) => cache.set(key, item));
    for (const key of Array.from(cache.keys())) {
        if (!userStates[key]) cache.delete(key);
    }

    return diff;
}
